// Live-draft preview plumbing, lets the Website admin show UNSAVED changes on
// the real site instantly.
//
// RECEIVER (this module): when the venue site runs inside an IFRAME on the same
// origin (the admin's live preview), it listens for postMessage drafts and
// exposes them as an appConfig OVERLAY that useVenue() merges over the fetched
// config. Nothing is written anywhere, close the preview and the draft is
// gone; the live site for real visitors is untouched until Save.
//
// Security: the listener only attaches when framed, and messages are accepted
// from the SAME ORIGIN only (the admin lives on the venue's own subdomain).
import { useSyncExternalStore } from "react";
import type { VenueAppConfig } from "./types";

export const PREVIEW_MSG_TYPE = "assembly:preview-config";
// The editor can also drive which URL the preview iframe is on, so selecting
// the "Contact" page tab shows /contact inside the frame, even for pages that
// don't yet exist in the saved config (draft-only additions).
export const PREVIEW_NAVIGATE_MSG_TYPE = "assembly:preview-navigate";

let overlay: Partial<VenueAppConfig> | null = null;
const subs = new Set<() => void>();

if (typeof window !== "undefined" && window.self !== window.top) {
  window.addEventListener("message", (e: MessageEvent) => {
    if (e.origin !== window.location.origin) return;
    const d = e.data as { type?: string; appConfig?: Partial<VenueAppConfig>; path?: string } | null;
    if (!d) return;
    if (d.type === PREVIEW_MSG_TYPE) {
      overlay = d.appConfig && Object.keys(d.appConfig).length > 0 ? d.appConfig : null;
      subs.forEach((f) => f());
      return;
    }
    if (d.type === PREVIEW_NAVIGATE_MSG_TYPE && typeof d.path === "string") {
      const target = d.path.startsWith("/") ? d.path : `/${d.path}`;
      if (target !== window.location.pathname) {
        // pushState + a fresh PopStateEvent, how TanStack Router's browser
        // history picks up an external URL change without a full reload.
        window.history.pushState({}, "", target);
        window.dispatchEvent(new PopStateEvent("popstate"));
      }
    }
  });
}

/** The draft appConfig overlay (null outside the admin preview iframe). */
export function usePreviewOverlay(): Partial<VenueAppConfig> | null {
  return useSyncExternalStore(
    (cb) => {
      subs.add(cb);
      return () => subs.delete(cb);
    },
    () => overlay,
    () => null,
  );
}
